import React, { useEffect, useState, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import confetti from 'canvas-confetti';
import './LuckyWheel.css';

const socket = io();

const STRIP_HEIGHT = 120; 
const CHAR_MAP = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".split('');
const REPEAT_COUNT = 150; // Dải băng dài gấp 150 lần bộ ký tự

const LuckyBoard = () => {
  const [stats, setStats] = useState(0);
  const [winners, setWinners] = useState([]);
  const [isSpinning, setIsSpinning] = useState(false);
  const [popupWinner, setPopupWinner] = useState(null);
  const [mode, setMode] = useState('wheel'); 
  const [rotation, setRotation] = useState(0);
  const [slotOffsets, setSlotOffsets] = useState([0, 0, 0, 0, 0, 0]); 
  
  // State cài đặt
  const [showSettings, setShowSettings] = useState(false);
  const [bgImage, setBgImage] = useState('https://img.freepik.com/free-vector/gradient-chinese-new-year-background_23-2151034598.jpg');
  const [overlayOpacity, setOverlayOpacity] = useState(0.4);
  const [uploadingBg, setUploadingBg] = useState(false);

  const staticStrip = useRef([]);

  useEffect(() => {
    // Tạo dải số tĩnh
    let arr = [];
    for(let i=0; i<REPEAT_COUNT; i++) arr = [...arr, ...CHAR_MAP];
    staticStrip.current = arr;

    fetchStats();
    fetchWinners();
    fetchSettings();

    socket.on('stats_update', () => fetchStats());
    socket.on('spin_result', (data) => startAnimation(data[0]));
    socket.on('settings_update', (newSettings) => {
        if(newSettings.bgImage) setBgImage(newSettings.bgImage);
        if(newSettings.overlayOpacity !== undefined) setOverlayOpacity(newSettings.overlayOpacity);
    });
    socket.on('reset_data', () => { setWinners([]); alert("Hệ thống đã reset!"); });
    
    return () => socket.off();
  }, [mode, rotation]);

  const fetchStats = async () => { try { const res = await axios.get('/api/stats'); setStats(res.data.total); } catch(e) {} };
  const fetchWinners = async () => { const res = await axios.get('/api/winners'); setWinners(res.data); };
  const fetchSettings = async () => {
      try {
          const res = await axios.get('/api/settings');
          if(res.data.bgImage) setBgImage(res.data.bgImage);
          if(res.data.overlayOpacity !== undefined) setOverlayOpacity(res.data.overlayOpacity);
      } catch(e) {}
  };

  const handleReset = async () => {
    if (window.confirm("⚠️ CẢNH BÁO: XÓA TOÀN BỘ KẾT QUẢ?")) {
        try { await axios.post('/api/reset'); } catch (e) { alert("Lỗi"); }
    }
  };

  const handleSpinClick = async () => {
    if (isSpinning) return;
    try {
        const res = await axios.post('/api/spin', { prize_name: 'Giải May Mắn' });
        if (!res.data.success) { alert(res.data.message); return; }
    } catch (e) { alert('Lỗi kết nối'); }
  };

  // Cài đặt giao diện
  const handleBgUpload = async (e) => {
      const file = e.target.files[0];
      if(!file) return;
      const formData = new FormData();
      formData.append('bgFile', file);
      formData.append('overlayOpacity', overlayOpacity);
      setUploadingBg(true);
      try {
          const res = await axios.post('/api/settings', formData, { headers: {'Content-Type': 'multipart/form-data'} });
          setBgImage(res.data.settings.bgImage);
      } catch(err) { alert("Lỗi upload"); } finally { setUploadingBg(false); }
  };

  const saveOpacitySettings = async () => { try { await axios.post('/api/settings', { overlayOpacity }); } catch(e) {} };

  // --- ANIMATION CORE ---
  const startAnimation = (winnerData) => {
    // Chưa bật trạng thái quay vội, để xử lý vị trí trước
    setPopupWinner(null);
    if (mode === 'wheel') runWheelAnimation(winnerData);
    else runSlotAnimation(winnerData);
  };

  const runWheelAnimation = (winnerData) => {
    setIsSpinning(true);
    const newRotation = rotation + 1800 + Math.floor(Math.random() * 360);
    setRotation(newRotation);
    setTimeout(() => { finishSpin(winnerData); }, 4000);
  };

  // --- LOGIC SLOT MACHINE THÔNG MINH (TELEPORT) ---
  const runSlotAnimation = (winnerData) => {
    const code = winnerData.employee_code.toString().toUpperCase();
    let chars = code.split('');
    while (chars.length < 6) chars.unshift('0');
    if (chars.length > 6) chars = chars.slice(-6);

    // BƯỚC 1: TELEPORT (NHẢY DÙ)
    // Tính toán vị trí tương đương ở đầu dải băng (Modulo)
    // Mục đích: Đưa vị trí hiện tại về gần số 0 nhất mà không thay đổi hình ảnh hiển thị
    const singleSetHeight = CHAR_MAP.length * STRIP_HEIGHT;
    const resetOffsets = slotOffsets.map(offset => offset % singleSetHeight);
    
    // Cập nhật vị trí reset ngay lập tức (Lúc này Transition đang tắt vì isSpinning = false)
    setSlotOffsets(resetOffsets);

    // BƯỚC 2: BẮT ĐẦU QUAY (SAU KHI ĐÃ RESET)
    // Dùng setTimeout nhỏ để Browser kịp render vị trí reset
    setTimeout(() => {
        setIsSpinning(true); // Bật Transition lên

        const finalOffsets = chars.map((char, colIndex) => {
            let charIdx = CHAR_MAP.indexOf(char);
            if (charIdx === -1) charIdx = 0;
            
            // Công thức: Đích đến luôn nằm ở xa tít phía dưới (Index ~140)
            // Đảm bảo quãng đường luôn là ~140 vòng quay
            const targetSetIndex = REPEAT_COUNT - 10 + (colIndex % 3); 
            
            // Cộng thêm offset hiện tại để đảm bảo tính liên tục (nếu cần), 
            // nhưng vì ta đã reset về đầu nên chỉ cần tính đích đến tuyệt đối.
            return (targetSetIndex * CHAR_MAP.length + charIdx) * STRIP_HEIGHT;
        });

        setSlotOffsets(finalOffsets);

        // Dừng sau 6 giây
        setTimeout(() => { finishSpin(winnerData); }, 6000); 
    }, 50);
  };

  const finishSpin = (winnerData) => {
    setIsSpinning(false);
    setPopupWinner(winnerData);
    fireConfetti();
    fetchWinners();
  };

  const fireConfetti = () => {
    var duration = 3 * 1000;
    var defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 2000 };
    confetti({ ...defaults, particleCount: 150, origin: { y: 0.6 } });
  };

  return (
    <div className="lucky-container" style={{ backgroundImage: `url('${bgImage}')` }}>
      <div className="overlay-bg" style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}></div>
      
      <div className="content-wrapper">
          {/* HEADER */}
          <div style={{ padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white' }}>
            <div style={{display:'flex', alignItems:'center', gap: 20}}>
                <h1 style={{ margin: 0, fontSize: 32, textShadow: '0 2px 4px rgba(0,0,0,0.5)', letterSpacing: 1 }}>🎉 LUCKY DRAW</h1>
                <button onClick={handleReset} style={{padding:'6px 12px', fontSize: 13, background:'#c0392b', color:'white', border:'1px solid #fff', borderRadius:4, cursor:'pointer', opacity: 0.8}}>⚙️ RESET</button>
                
                {/* SETTINGS BUTTON */}
                <div style={{position: 'relative'}}>
                    <button className="btn-toggle-settings" onClick={() => setShowSettings(!showSettings)}>🎨</button>
                    {showSettings && (
                        <div className="settings-panel">
                            <button className="btn-close-settings" onClick={() => setShowSettings(false)}>✕</button>
                            <h4 style={{marginTop:0, borderBottom:'1px solid #555', paddingBottom:5}}>Cài đặt giao diện</h4>
                            <div className="settings-group">
                                <label>Ảnh nền:</label>
                                <label className="btn-upload">
                                    {uploadingBg ? 'Đang tải...' : '📁 Chọn ảnh'}
                                    <input type="file" accept="image/*" style={{display:'none'}} onChange={handleBgUpload} disabled={uploadingBg} />
                                </label>
                            </div>
                            <div className="settings-group">
                                <label>Độ tối nền: {Math.round(overlayOpacity * 100)}%</label>
                                <input type="range" min="0" max="0.9" step="0.1" value={overlayOpacity} onChange={(e) => setOverlayOpacity(e.target.value)} onMouseUp={saveOpacitySettings} onTouchEnd={saveOpacitySettings} className="range-slider" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <div style={{background:'rgba(255,255,255,0.2)', padding: 5, borderRadius: 30, display:'flex', backdropFilter: 'blur(5px)'}}>
                <button onClick={() => !isSpinning && setMode('wheel')} style={{padding: '10px 25px', borderRadius: 25, border:'none', cursor:'pointer', fontWeight:'bold', background: mode==='wheel' ? '#f1c40f' : 'transparent', color: mode==='wheel'?'#333':'#fff', transition:'all 0.3s'}}>🎡 Vòng Quay</button>
                <button onClick={() => !isSpinning && setMode('slot')} style={{padding: '10px 25px', borderRadius: 25, border:'none', cursor:'pointer', fontWeight:'bold', background: mode==='slot' ? '#f1c40f' : 'transparent', color: mode==='slot'?'#333':'#fff', transition:'all 0.3s'}}>🎰 Quay Số</button>
            </div>

            <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 16, opacity: 0.9, display:'block' }}>Người tham gia</span>
                <span style={{ fontSize: 32, fontWeight: 'bold', color: '#f1c40f' }}>{stats}</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', padding: 20, gap: 30, justifyContent:'center', marginTop: 20 }}>
            <div style={{ flex: 2, minWidth: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '500px' }}>
                
                {mode === 'wheel' && (
                    <div style={{ position: 'relative', transform: 'scale(1.1)' }}>
                        <div className="pointer"></div>
                        <div className="wheel-container" style={{ transform: `rotate(${rotation}deg)` }}>
                            <div className="wheel-section"></div>
                            <div className="center-circle">GO</div>
                        </div>
                    </div>
                )}

                {mode === 'slot' && (
                    <div className="slot-machine-frame">
                        {[0,1,2,3,4,5].map((colIndex) => (
                            <div key={colIndex} className="slot-column">
                                <div 
                                    className={`slot-strip ${isSpinning ? 'is-spinning' : ''}`}
                                    style={{ 
                                        transform: `translateY(-${slotOffsets[colIndex]}px)`,
                                        /* Logic Transition: Chỉ bật khi isSpinning = true */
                                        transition: isSpinning 
                                            ? `transform ${4.5 + (colIndex * 0.3)}s cubic-bezier(0.1, 0.7, 0.1, 1)` 
                                            : 'none'
                                    }}
                                >
                                    {staticStrip.current.map((char, idx) => (
                                        <div key={idx} className="slot-number">{char}</div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <button onClick={handleSpinClick} disabled={isSpinning} className={`btn-action ${isSpinning ? 'btn-disabled' : 'btn-spin'}`} style={{marginTop: 60}}>
                    {isSpinning ? 'ĐANG QUAY...' : 'QUAY THƯỞNG'}
                </button>
            </div>

            <div style={{ flex: 1, minWidth: '300px', maxWidth: '450px', background: 'rgba(255,255,255,0.9)', borderRadius: 15, padding: 20, maxHeight: '65vh', overflowY:'auto', border: '1px solid rgba(255,255,255,0.5)' }}>
                <h2 style={{ borderBottom: '2px solid #c0392b', paddingBottom: 10, color: '#c0392b', marginTop:0, textAlign:'center' }}>🏆 DANH SÁCH TRÚNG</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
                    <tbody>
                        {winners.map((w, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: 10 }}>
                                    {w.avatar_url ? <img src={'/uploads/' + w.avatar_url} alt="" style={{width: 50, height: 50, borderRadius: '50%', objectFit: 'cover', border: '2px solid #aaa'}} /> : <div style={{width:50, height:50, borderRadius:'50%', background:'#ccc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20}}>👤</div>}
                                </td>
                                <td style={{ padding: 10, fontWeight:'bold', color: '#c0392b', fontSize: 26, textAlign:'right', fontFamily: 'Impact' }}>{w.employee_code}</td>
                            </tr>
                        ))}
                        {winners.length === 0 && <tr><td colSpan="2" style={{padding:20, textAlign:'center', color:'#888', fontStyle:'italic'}}>Chưa có ai trúng giải</td></tr>}
                    </tbody>
                </table>
            </div>
          </div>
      </div>

      {popupWinner && (
        <div className="modal-overlay" onClick={() => setPopupWinner(null)}>
            <div className="winner-popup" onClick={(e) => e.stopPropagation()}>
                <h1 style={{ color: '#d4af37', margin: 0, textShadow: '1px 1px 2px #000', fontSize: 40 }}>🎉 CHÚC MỪNG 🎉</h1>
                <div style={{margin: '30px 0'}}>
                    {popupWinner.avatar_url ? <img src={'/uploads/' + popupWinner.avatar_url} alt="Avatar" style={{width: 250, height: 250, borderRadius: '50%', objectFit: 'cover', border: '8px solid #d4af37', boxShadow: '0 0 30px rgba(212, 175, 55, 0.5)'}} /> : <div style={{width: 200, height: 200, borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 80, margin:'0 auto', border: '5px solid #ccc'}}>🎁</div>}
                </div>
                <div style={{fontSize: 80, fontFamily: 'Impact', color: '#c0392b', letterSpacing: 2}}>{popupWinner.employee_code}</div>
                <div style={{ fontSize: 24, color: '#555', marginTop: 10 }}>Giải: <strong style={{color:'#e67e22'}}>{popupWinner.prize_name}</strong></div>
                <button onClick={() => setPopupWinner(null)} style={{marginTop: 30, padding: '12px 40px', fontSize: 18, background: '#333', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer'}}>Đóng lại</button>
            </div>
        </div>
      )}
    </div>
  );
};

export default LuckyBoard;
